import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import { CREDITS_QUERY_KEY } from 'hooks/useCredits';

export interface CreditTopUpPayload {
  credits: number;
  callback_url?: string;
  country?: string;
}

export interface CreditTopUpResponse {
  message: string;
  data: any;
}

/**
 * Hook to top up credits
 */
export const useCreditTopUp = () => {
  const { openToast } = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (payload: CreditTopUpPayload) => {
      const response = await apiClient.post<CreditTopUpResponse>(
        '/billing/credits/top-up/',
        payload
      );
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: CREDITS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['creditUsage'] });

      openToast({
        show: true,
        heading: 'Success',
        text: data.message || 'Credit top-up successful',
        type: 'success',
        timeOut: 5000,
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.message || 'Failed to top up credits';

      openToast({
        show: true,
        heading: 'Error',
        text: errorMessage,
        type: 'error',
        timeOut: 5000,
      });
    },
  });

  return {
    topUpCredits: mutation.mutate,
    topUpCreditsAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    data: mutation.data,
    error: mutation.error,
    reset: mutation.reset,
  };
};
